import { PdBaseUIInput } from "../base/pd-base-ui-input.js";
import { css, html } from "lit";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-radio-group/PdRadioGroup.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Radio group container for pd-checkbox elements with radio behavior.
*
* @tagname pd-radio-group
*
* @event validate-form - Fired when selection changes for validation.
* @event field-change - Fired when selection changes.
*
* @slot - pd-checkbox elements to display as radio options.
*
* @cssprop --pd-cb-group-gap - Gap between radio options. Default: `20px`.
* @cssprop --pd-cb-group-direction - Flex direction for layout. Default: `row`.
* @cssprop --pd-input-label-padding - Label padding. Default: `0`.
* @cssprop --pd-cb-border-col - Border color for child checkboxes in readonly state. Default: `var(--pd-default-col)`.
* @cssprop --pd-cb-border-col-readonly - Readonly border color forwarded to child checkboxes. Default: `var(--pd-cb-border-col, var(--pd-default-col))`.
*/
var PdRadioGroup = class extends PdBaseUIInput {
	static {
		this.styles = [PdBaseUIInput.styles, css`
      .group-style {
        display: flex;
        gap: var(--pd-cb-group-gap, 20px);
        flex-direction: var(--pd-cb-group-direction, row);
        flex-wrap: wrap;
        padding: var(--pd-input-label-padding, 0);
        padding-top: 5px;
      }

      ::slotted(pd-checkbox) {
        --pd-cb-border-col-readonly: var(
          --pd-cb-border-col,
          var(--pd-default-col)
        );
      }
    `];
	}
	firstUpdated() {
		this.addEventListener("pd-form-element-register", this._onInternalRegister);
		this.addEventListener("pd-form-element-change", this._onInternalChange);
		this.addEventListener("pd-form-element-focus", this._onInternalFocus);
		this.addEventListener("pd-form-element-blur", this._onInternalBlur);
		super.firstUpdated();
	}
	render() {
		const radioInputId = `${this.id}RadioGroup`;
		return html`
      ${this._renderLabel(radioInputId)}
      <div
        id="${radioInputId}"
        role="radiogroup"
        aria-label="${this.label || ""}"
        aria-disabled="${this.disabled}"
        aria-readonly="${this.readonly}"
        @keydown="${this._onKeyDown}"
        class="${classMap(this.getClassmap({
			"group-style": true,
			input: true
		}))}"
      >
        <slot></slot>
      </div>
      ${this._renderErrorMsg()}
    `;
	}
	_onKeyDown(event) {
		if (this.disabled || this.readonly) return;
		const checkboxes = Array.from(this.querySelectorAll("pd-checkbox"));
		if (checkboxes.length === 0) return;
		const focusedIndex = checkboxes.findIndex((el) => el.shadowRoot?.activeElement || el.matches(":focus-within"));
		let nextIndex = -1;
		switch (event.key) {
			case "ArrowDown":
			case "ArrowRight":
				event.preventDefault();
				nextIndex = focusedIndex < checkboxes.length - 1 ? focusedIndex + 1 : 0;
				break;
			case "ArrowUp":
			case "ArrowLeft":
				event.preventDefault();
				nextIndex = focusedIndex > 0 ? focusedIndex - 1 : checkboxes.length - 1;
				break;
			case "Home":
				event.preventDefault();
				nextIndex = 0;
				break;
			case "End":
				event.preventDefault();
				nextIndex = checkboxes.length - 1;
				break;
			default: return;
		}
		if (nextIndex >= 0 && nextIndex < checkboxes.length) {
			const radioControl = checkboxes[nextIndex].shadowRoot?.querySelector("[role=\"radio\"]");
			if (radioControl) radioControl.focus();
		}
	}
	_onInternalBlur(e) {
		e.stopPropagation();
	}
	_onInternalFocus(e) {
		e.stopPropagation();
	}
	_onInternalChange(e) {
		if (e.target !== this) {
			e.stopPropagation();
			const target = e.target;
			const targetValue = e.detail?.value;
			if (targetValue === "false" || targetValue === false) return;
			const checkboxes = this.querySelectorAll("pd-checkbox");
			let newValue;
			checkboxes.forEach((el) => {
				const isActive = el === target;
				el.readonly = isActive;
				el.setCheckedState(isActive ? "true" : "false");
				if (isActive) newValue = el.valueName;
			});
			const controlled = e.detail?.state ? e.detail.state.dirty === false : false;
			this._handleChangedValue(newValue || "", void 0, true, false, void 0, controlled);
		}
	}
	_onInternalRegister(e) {
		if (e.target !== this) {
			e.stopPropagation();
			if (e.detail.value === "true") this._handleChangedValue(e.detail.name, void 0, true, false, void 0, true);
		}
	}
	_onInternalUnRegister(e) {
		if (e.target !== this) e.stopPropagation();
	}
};
//#endregion
export { PdRadioGroup };
